import React, { useCallback, useMemo, useState } from "react";
import { Checkbox, Tooltip } from "antd";
import { DownOutlined, RightOutlined } from "@ant-design/icons";
import { ZoneNode } from "../../../types/sortingMachine";
import { collectAllDescendantIds } from "./buildZoneTree";

interface ZoneTreeNodeProps {
	node: ZoneNode;
	selectedZoneIds: Set<number>;
	disabledZoneMap: Map<number, string>;
	onToggle: (zoneIds: number[], checked: boolean) => void;
	onDisabledZoneAttempt?: (
		zoneName: string,
		assignmentDetail: string
	) => void;
	searchTerm: string;
	depth: number;
}

const matchesSearch = (node: ZoneNode, term: string): boolean => {
	if (node.description.toLowerCase().includes(term)) {
		return true;
	}
	return node.children.some((child) => matchesSearch(child, term));
};

type CheckState = "checked" | "unchecked" | "indeterminate";

const getCheckState = (
	node: ZoneNode,
	selectedIds: Set<number>,
	disabledMap: Map<number, string>
): CheckState => {
	const allIds = collectAllDescendantIds(node);
	const selectableIds = allIds.filter((id) => !disabledMap.has(id));

	if (selectableIds.length === 0) {
		return "unchecked";
	}

	const checkedCount = selectableIds.filter((id) =>
		selectedIds.has(id)
	).length;

	if (checkedCount === 0) {
		return "unchecked";
	}
	if (checkedCount === selectableIds.length) {
		return "checked";
	}
	return "indeterminate";
};

const CHECKBOX_SCALE = "scale-125";

const ZoneTreeNode = ({
	node,
	selectedZoneIds,
	disabledZoneMap,
	onToggle,
	onDisabledZoneAttempt,
	searchTerm,
	depth,
}: ZoneTreeNodeProps) => {
	const [expanded, setExpanded] = useState(depth < 1);

	const hasChildren = node.children.length > 0;
	const isDisabled = disabledZoneMap.has(node.id);
	const disabledTooltip = disabledZoneMap.get(node.id) ?? "";
	const lowerSearch = searchTerm.toLowerCase();
	const isVisible = lowerSearch === "" || matchesSearch(node, lowerSearch);

	const checkState = useMemo(
		() => getCheckState(node, selectedZoneIds, disabledZoneMap),
		[node, selectedZoneIds, disabledZoneMap]
	);

	const notifyDisabled = useCallback(
		(zoneName: string, detail: string) => {
			onDisabledZoneAttempt?.(zoneName, detail);
		},
		[onDisabledZoneAttempt]
	);

	const handleToggle = useCallback(() => {
		const allIds = collectAllDescendantIds(node);
		const selectableIds = allIds.filter((id) => !disabledZoneMap.has(id));
		if (selectableIds.length === 0 && allIds.length > 0) {
			const firstBlocked = allIds.find((id) => disabledZoneMap.has(id));
			const detail =
				firstBlocked !== undefined
					? disabledZoneMap.get(firstBlocked) ?? ""
					: "";
			notifyDisabled(node.description, detail);
			return;
		}
		const shouldCheck = checkState !== "checked";
		onToggle(selectableIds, shouldCheck);
	}, [node, disabledZoneMap, checkState, onToggle, notifyDisabled]);

	const handleExpandToggle = useCallback(() => {
		setExpanded((prev) => !prev);
	}, []);

	if (!isVisible) {
		return null;
	}

	const expandForSearch = lowerSearch !== "" && hasChildren;
	const isExpanded = expanded || expandForSearch;

	const isCountry = depth === 0;
	const isCity = depth === 1 && hasChildren;
	const isLeaf = !hasChildren;

	const checkboxNode = (
		<span className={CHECKBOX_SCALE}>
			<Checkbox
				checked={checkState === "checked"}
				indeterminate={checkState === "indeterminate"}
				onChange={() => handleToggle()}
				className={isLeaf && isDisabled ? "opacity-60" : undefined}
			/>
		</span>
	);

	if (isCountry) {
		return (
			<div className={depth === 0 ? "mb-3" : ""}>
				<div
					className="flex items-center gap-3 py-2.5 px-4 rounded-lg bg-gray-100 border border-gray-200 cursor-pointer"
					onClick={handleExpandToggle}
				>
					<span className="flex items-center justify-center w-5 h-5 text-gray-500 shrink-0">
						{isExpanded ? (
							<DownOutlined style={{ fontSize: 12 }} />
						) : (
							<RightOutlined style={{ fontSize: 12 }} />
						)}
					</span>

					<span onClick={(e) => e.stopPropagation()}>
						{isDisabled && isLeaf ? (
							<Tooltip title={disabledTooltip}>
								{checkboxNode}
							</Tooltip>
						) : (
							checkboxNode
						)}
					</span>

					<span className="text-sm font-bold text-gray-800 select-none flex-1">
						{node.description}
					</span>

					{hasChildren && (
						<span className="text-xs font-medium text-gray-400 bg-gray-200 px-2 py-0.5 rounded-full">
							{node.children.length}
						</span>
					)}
				</div>

				{hasChildren && isExpanded && (
					<div className="ml-6 mt-1 pl-4 border-l-2 border-gray-200">
						{node.children.map((child) => (
							<ZoneTreeNode
								key={child.id}
								node={child}
								selectedZoneIds={selectedZoneIds}
								disabledZoneMap={disabledZoneMap}
								onToggle={onToggle}
								onDisabledZoneAttempt={onDisabledZoneAttempt}
								searchTerm={searchTerm}
								depth={depth + 1}
							/>
						))}
					</div>
				)}
			</div>
		);
	}

	if (isCity) {
		return (
			<div className="mt-1">
				<div
					className="flex items-center gap-3 py-2 px-3 rounded-md bg-blue-50 border-l-3 border-blue-400 cursor-pointer hover:bg-blue-100"
					onClick={handleExpandToggle}
				>
					<span className="flex items-center justify-center w-4 h-4 text-blue-400 shrink-0">
						{isExpanded ? (
							<DownOutlined style={{ fontSize: 11 }} />
						) : (
							<RightOutlined style={{ fontSize: 11 }} />
						)}
					</span>

					<span onClick={(e) => e.stopPropagation()}>
						{checkboxNode}
					</span>

					<span className="text-sm font-semibold text-gray-700 select-none flex-1">
						{node.description}
					</span>

					<span className="text-xs text-blue-400 font-medium">
						{node.children.length}
					</span>
				</div>

				{isExpanded && (
					<div className="ml-5 mt-0.5 pl-4 border-l-2 border-blue-200">
						{node.children.map((child) => (
							<ZoneTreeNode
								key={child.id}
								node={child}
								selectedZoneIds={selectedZoneIds}
								disabledZoneMap={disabledZoneMap}
								onToggle={onToggle}
								onDisabledZoneAttempt={onDisabledZoneAttempt}
								searchTerm={searchTerm}
								depth={depth + 1}
							/>
						))}
					</div>
				)}
			</div>
		);
	}

	const leafDisabledClass = isDisabled
		? "text-gray-400 line-through"
		: "text-gray-600";

	return (
		<div
			className="flex items-center gap-3 py-1.5 px-3 rounded hover:bg-gray-50 cursor-pointer"
			onClick={() => handleToggle()}
		>
			<span className="w-4 shrink-0" />

			<span onClick={(e) => e.stopPropagation()}>
				{isDisabled ? (
					<Tooltip title={disabledTooltip}>{checkboxNode}</Tooltip>
				) : (
					checkboxNode
				)}
			</span>

			<span className={`text-sm select-none ${leafDisabledClass}`}>
				{node.description}
			</span>
		</div>
	);
};

export default React.memo(ZoneTreeNode);
